import { create } from 'zustand';
import type { IncidentSeverity } from '@tiw/shared-types';

/**
 * Incident store.
 *
 * Tracks currently-open *real* incidents (IODA country outages, provider status)
 * keyed by id. Reconciled by the `useIncidents` poll of `/v1/incidents`; the
 * globe renders a pulse at each geolocated incident. See docs/BUILD_PLAN.md (P3).
 */
export interface ActiveIncident {
  readonly id: string;
  readonly severity: IncidentSeverity;
  readonly title: string;
  /** Origin feed, e.g. `ioda` or `cloudflare-status`. */
  readonly source: string;
  /** Globe location, if geolocatable (else null — event-log only). */
  readonly lat: number | null;
  readonly lon: number | null;
}

interface IncidentState {
  readonly active: Readonly<Record<string, ActiveIncident>>;
  open: (incident: ActiveIncident) => void;
  close: (id: string) => void;
}

export const useIncidentStore = create<IncidentState>((set) => ({
  active: {},
  open: (incident) =>
    set((state) => ({ active: { ...state.active, [incident.id]: incident } })),
  close: (id) =>
    set((state) => {
      if (!(id in state.active)) return state;
      const next = { ...state.active };
      delete next[id];
      return { active: next };
    }),
}));
